import { Paper } from '@mui/material';
import type { InvoiceOverview } from '@weft/api-definition';
import { validateNotNullOrUndefined } from '@weft/utils';
import { withGlobalSearchParams } from 'apps/app/src/components/utility/GlobalSearchParamsBoundary';
import { getCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import React from 'react';
import { Helmet } from 'react-helmet';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useCustomer } from '../../../hooks/requests/use-customer';
import { useInvoicesOverviews } from '../../../hooks/requests/use-invoices-overviews/use-invoices-overviews';
import { useInvoiceStatistics } from '../../../hooks/requests/use-invoices-statistics';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { InvoicesPageInfoCards } from '../../invoices/InvoicesPage/invoices-page-components/InvoicesPageInfoCards';
import { CustomerInvoicesTable } from './customer-invoices-page-components/CustomerInvoicesTable';
import { customerInvoicesOverviewsPageSize } from './customer-invoices-page-consts/customer-invoices-overviews-page-size';

const CustomerInvoicesPage: React.FC = () => {
  const { customerId } = useValidatedParams<['customerId']>('customerId');
  const { data: invoicesOverviewsResponse, isFetching: isFetchingInvoices } = useInvoicesOverviews({ customerId, pageSize: customerInvoicesOverviewsPageSize }, {
    suspense: true,
  });
  const invoicesOverviews = invoicesOverviewsResponse?.invoicesOverviews ?? [];
  const customerResponse = useCustomer(customerId, { suspense: true });
  validateNotNullOrUndefined(customerResponse.data);
  const { customer } = customerResponse.data;
  const currencySymbol = getCurrencySymbol(customer.settings?.currency);
  const invoicesWithCurrency: InvoiceOverview[] = invoicesOverviews.map(invoice => ({ ...invoice, currencySymbol }));

  const { data: invoicesStatistics } = useInvoiceStatistics(customerId);

  return (
    <>
      <Helmet>
        <title>Customer Invoices</title>
      </Helmet>

      <InvoicesPageInfoCards
        invoicesOverviewsSummary={invoicesStatistics?.summary}
      />

      <Paper sx={{
        borderRadius: '10px',
        height: 'calc(100% - 96px)',
        overflow: 'hidden',
        marginTop: '24px',
      }}
      >
        <div className="flex items-center gap-1.5 px-4 pt-4 text-lg">
          <div className="font-semibold">{invoicesOverviews.length}</div>
          <div className="text-soft-title">Total Invoices</div>
        </div>

        <CustomerInvoicesTable
          items={invoicesWithCurrency}
          itemsCount={invoicesOverviews.length}
          loadingItems={isFetchingInvoices}
        />
      </Paper>
    </>
  );
};

const CustomerInvoicesWithGlobalSearchParams = withGlobalSearchParams(CustomerInvoicesPage);

const CustomerInvoicesPageWithSuspenseBoundary = withSuspenseBoundary(CustomerInvoicesWithGlobalSearchParams);

export { CustomerInvoicesPageWithSuspenseBoundary as CustomerInvoicesPage };